import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';

/**
 * Markdown parser instance configured with table support and security options
 */
const md = new MarkdownIt({
    html: false, // Disable HTML in markdown for security
    breaks: true, // Convert line breaks to <br>
    linkify: true, // Auto-convert URLs to links
    typographer: true, // Enable typographic replacements
});

/**
 * DOMPurify configuration for safe HTML sanitization
 */
const purifyConfig = {
    ALLOWED_TAGS: [
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'p',
        'br',
        'strong',
        'b',
        'em',
        'i',
        'ul',
        'ol',
        'li',
        'blockquote',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
        'code',
        'pre',
        'a',
    ],
    ALLOWED_ATTR: ['href', 'target'],
    ALLOW_DATA_ATTR: false,
};

/**
 * Parse markdown text to safe HTML
 *
 * @param markdownText - The markdown text to parse
 * @returns Sanitized HTML string
 */
export const parseMarkdown = (markdownText: string): string => {
    if (!markdownText?.trim()) {
        return '';
    }

    try {
        // Parse markdown to HTML
        const html = md.render(markdownText);

        // Sanitize HTML to prevent XSS attacks
        const cleanHtml = DOMPurify.sanitize(html, purifyConfig);

        return cleanHtml;
    } catch (error) {
        console.error('Error parsing markdown:', error);

        // Return escaped plain text as fallback
        return markdownText.replace(/[&<>"']/g, match => {
            const escapeMap: Record<string, string> = {
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                '\'': '&#x27;',
            };

            return escapeMap[match] || match;
        });
    }
};

/**
 * Check if text contains markdown syntax
 *
 * @param text - The text to check
 * @returns True if text appears to contain markdown
 */
export const isMarkdown = (text: string): boolean => {
    if (!text?.trim()) {
        return false;
    }

    // Check for common markdown patterns
    const markdownPatterns = [
        /^#{1,6}\s/, // Headers
        /^\s*[-*+]\s/, // Unordered lists
        /^\s*\d+\.\s/, // Ordered lists
        /\|.*\|/, // Tables
        /`.*`/, // Inline code
        /```/, // Code blocks
        /\*\*.*\*\*/, // Bold
        /__.*__/, // Bold
        /\*.*\*/, // Italic
        /_.*_/, // Italic
    ];

    return markdownPatterns.some(pattern => pattern.test(text));
};
